import {
  ensureContactByName,
  searchContacts,
  type ContactRow,
} from "../supabase/contacts";
import {
  getActivePassengerSegment,
  replaceActiveToken,
} from "./passengerSegment";

const DEBOUNCE_MS = 200;

function isPassengerOrCrewAcInput(el: HTMLInputElement): boolean {
  return el.dataset.f === "passengers" || el.dataset.f === "crew";
}

function hideList(ul: HTMLUListElement): void {
  ul.classList.add("hidden");
  ul.innerHTML = "";
  ul.removeAttribute("aria-activedescendant");
}

function positionList(ul: HTMLUListElement, input: HTMLInputElement): void {
  const r = input.getBoundingClientRect();
  ul.style.position = "fixed";
  ul.style.left = `${r.left}px`;
  ul.style.top = `${r.bottom + 2}px`;
  ul.style.minWidth = `${r.width}px`;
  ul.style.zIndex = "100";
}

export function initPassengersAutocomplete(
  root: HTMLElement,
  options: { contactsSearchEnabled: boolean }
): void {
  const state = {
    /** Browser `setTimeout` id (typed as number for tsc with Node typings in the toolchain). */
    timer: undefined as number | undefined,
    activeInput: null as HTMLInputElement | null,
    activeList: null as HTMLUListElement | null,
    items: [] as ContactRow[],
    activeIndex: -1,
  };

  function closeActive(): void {
    if (state.activeList) hideList(state.activeList);
    state.activeInput = null;
    state.activeList = null;
    state.items = [];
    state.activeIndex = -1;
  }

  function ensureListEl(input: HTMLInputElement): HTMLUListElement {
    let ul = input.parentElement?.querySelector<HTMLUListElement>(
      ".passengers-ac-list"
    );
    if (!ul) {
      ul = document.createElement("ul");
      ul.className = "passengers-ac-list hidden";
      ul.setAttribute("role", "listbox");
      ul.setAttribute("aria-label", "Contact matches");
      input.parentElement?.appendChild(ul);
    }
    return ul;
  }

  /** Non-selectable feedback (no Supabase, API error, or zero matches). */
  function renderInfoList(input: HTMLInputElement, message: string): void {
    const ul = ensureListEl(input);
    state.activeInput = input;
    state.activeList = ul;
    state.items = [];
    state.activeIndex = -1;
    ul.innerHTML = "";
    ul.removeAttribute("aria-activedescendant");
    const li = document.createElement("li");
    li.className = "passengers-ac-item passengers-ac-info";
    li.setAttribute("role", "note");
    li.textContent = message;
    ul.appendChild(li);
    ul.classList.remove("hidden");
    positionList(ul, input);
  }

  function renderList(
    input: HTMLInputElement,
    contacts: ContactRow[]
  ): void {
    const ul = ensureListEl(input);
    state.activeInput = input;
    state.activeList = ul;
    state.items = contacts;
    state.activeIndex = -1;
    ul.innerHTML = "";
    if (contacts.length === 0) {
      hideList(ul);
      return;
    }
    if (!input.dataset.acId) {
      input.dataset.acId = `pax-${Math.random().toString(36).slice(2, 11)}`;
    }
    const idBase = input.dataset.acId;
    contacts.forEach((c, i) => {
      const li = document.createElement("li");
      li.id = `passengers-ac-${idBase}-${i}`;
      li.className = "passengers-ac-item";
      li.setAttribute("role", "option");
      li.dataset.index = String(i);
      const meta = [c.email, c.phone].filter(Boolean).join(" · ");
      const sub = meta
        ? ` <span class="passengers-ac-meta">${escapeHtml(meta)}</span>`
        : "";
      li.innerHTML = `<span class="passengers-ac-name">${escapeHtml(c.name)}</span>${sub}`;
      li.addEventListener("mousedown", (e) => {
        e.preventDefault();
        applyPick(input, c.name);
      });
      ul!.appendChild(li);
    });
    ul.classList.remove("hidden");
    positionList(ul, input);
  }

  function applyPick(input: HTMLInputElement, name: string): void {
    const cur = input.selectionStart ?? input.value.length;
    const { newValue, newCursor } = replaceActiveToken(input.value, cur, name);
    input.value = newValue;
    input.setSelectionRange(newCursor, newCursor);
    void ensureContactByName(name);
    closeActive();
    input.focus();
  }

  function highlightIndex(ul: HTMLUListElement, idx: number): void {
    const lis = ul.querySelectorAll<HTMLLIElement>(".passengers-ac-item");
    lis.forEach((li, i) => {
      li.classList.toggle("passengers-ac-active", i === idx);
      li.setAttribute("aria-selected", i === idx ? "true" : "false");
    });
    const input = state.activeInput;
    if (input && lis[idx]) {
      input.setAttribute("aria-activedescendant", lis[idx]!.id);
    }
  }

  async function runSearch(input: HTMLInputElement): Promise<void> {
    const cur = input.selectionStart ?? input.value.length;
    const { tokenQuery } = getActivePassengerSegment(input.value, cur);
    if (tokenQuery.length < 1) {
      const ul = input.parentElement?.querySelector<HTMLUListElement>(
        ".passengers-ac-list"
      );
      if (ul) hideList(ul);
      return;
    }
    if (!options.contactsSearchEnabled) {
      renderInfoList(
        input,
        "Cloud contacts search is off (Supabase not set in this build). Type full names; separate with ; ."
      );
      return;
    }
    const { data, error } = await searchContacts(tokenQuery, 15);
    if (error || !data) {
      renderInfoList(
        input,
        error ? `Could not load contacts: ${error}` : "Could not load contacts."
      );
      return;
    }
    if (data.length === 0) {
      renderInfoList(
        input,
        `No saved contact matches “${tokenQuery}”. Import contacts on the Data tab, or keep typing the name.`
      );
      return;
    }
    renderList(input, data);
  }

  root.addEventListener("input", (e) => {
    const t = e.target;
    if (!(t instanceof HTMLInputElement) || !isPassengerOrCrewAcInput(t)) {
      return;
    }
    window.clearTimeout(state.timer);
    state.timer = window.setTimeout(() => {
      void runSearch(t);
    }, DEBOUNCE_MS);
  });

  root.addEventListener(
    "keydown",
    (e) => {
      const t = e.target;
      if (!(t instanceof HTMLInputElement) || !isPassengerOrCrewAcInput(t)) {
        return;
      }
      const ul = t.parentElement?.querySelector<HTMLUListElement>(
        ".passengers-ac-list"
      );
      if (!ul || ul.classList.contains("hidden") || state.items.length === 0) {
        if (e.key === "Escape") closeActive();
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        hideList(ul);
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        state.activeIndex = Math.min(
          state.items.length - 1,
          state.activeIndex + 1
        );
        highlightIndex(ul, state.activeIndex);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        state.activeIndex = Math.max(0, state.activeIndex - 1);
        highlightIndex(ul, state.activeIndex);
        return;
      }
      if (e.key === "Enter") {
        const idx =
          state.activeIndex >= 0 ? state.activeIndex : state.items.length > 0 ? 0 : -1;
        if (idx >= 0) {
          e.preventDefault();
          const pick = state.items[idx]?.name;
          if (pick) applyPick(t, pick);
        }
      }
    },
    true
  );

  root.addEventListener(
    "blur",
    (e) => {
      const t = e.target;
      if (!(t instanceof HTMLInputElement) || !isPassengerOrCrewAcInput(t)) {
        return;
      }
      window.setTimeout(() => {
        const ae = document.activeElement;
        if (ae && t.parentElement?.contains(ae)) return;
        const ul = t.parentElement?.querySelector(".passengers-ac-list");
        if (ul && ae && ul.contains(ae)) return;
        const cur = t.selectionStart ?? t.value.length;
        const { tokenQuery } = getActivePassengerSegment(t.value, cur);
        if (tokenQuery.length >= 3) {
          void ensureContactByName(tokenQuery);
        }
        closeActive();
      }, 200);
    },
    true
  );

  window.addEventListener("scroll", () => {
    if (state.activeList && state.activeInput) {
      positionList(state.activeList, state.activeInput);
    }
  });
  window.addEventListener("resize", () => {
    if (state.activeList && state.activeInput) {
      positionList(state.activeList, state.activeInput);
    }
  });
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
